import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrap, FadeIn } from '../components/Section';

const categories = [
  { icon: '🧱', title: 'Cement & Concrete', tests: ['Compressive strength of cubes', 'Cement setting time', 'Soundness testing', 'Mix design (M-10 to M-60+)', 'Core cutting & testing'] },
  { icon: '⛰️', title: 'Aggregates', tests: ['Sieve analysis & grading', 'Crushing Value (ACV)', 'Impact Value (AIV)', 'Los Angeles abrasion', 'Specific gravity'] },
  { icon: '🔩', title: 'Steel Reinforcement', tests: ['Tensile strength', 'Yield stress', 'Elongation', 'IS standard compliance', 'Weight per meter'] },
  { icon: '🔍', title: 'Non-Destructive Testing', tests: ['Ultrasonic Pulse Velocity', 'Rebound hammer', 'Integrity assessment', 'Strength evaluation', 'Crack depth measurement'] },
  { icon: '💧', title: 'Water Analysis', tests: ['Chloride content', 'Sulfate concentration', 'Organic impurities', 'pH and alkalinity', 'Construction suitability'] },
];

export default function Materials() {
  return (
    <PageWrap>
      <section style={{ padding: '160px 0 80px', background: 'var(--warm)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>Services</p>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 20 }}>
              Construction Material<br /><span style={{ color: 'var(--primary-light)' }}>Testing Services</span>
            </h1>
            <p style={{ fontSize: '1.08rem', color: 'var(--text-mid)', maxWidth: 650, lineHeight: 1.7, marginBottom: 32 }}>
              To comply with the National Building Code of India and relevant IS codes, all raw materials in civil construction must undergo physical and chemical testing. Our accredited laboratory evaluates the full range.
            </p>
            <Link to="/contact" className="btn btn-primary">Submit Materials for Testing</Link>
          </motion.div>
        </div>
      </section>

      {/* Standards Bar */}
      <section style={{ padding: '40px 0', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[['📋', 'National Building Code', 'Full NBC compliance'], ['🏛️', 'IS Standards', 'Indian Standards compliance'], ['✅', 'NABL Accredited', 'ISO/IEC 17025:2017']].map(([icon, title, sub]) => (
              <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 6 }}>
                <span style={{ fontSize: '1.4rem' }}>{icon}</span>
                <div><strong style={{ fontSize: '0.9rem', display: 'block' }}>{title}</strong><span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{sub}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Testing Capabilities</h2>
            <p style={{ color: 'var(--text-light)', marginBottom: 20, maxWidth: 560 }}>Comprehensive testing across five material categories under strict quality protocols.</p>
            <a href="/documents/test-list.docx" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: 'rgba(45,74,45,0.06)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.85rem', fontWeight: 500, color: 'var(--primary)', marginBottom: 48 }}>
              📄 Download Complete Test List
            </a>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {categories.map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.08}>
                <div style={{ padding: 32, background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: '100%', transition: 'all 0.3s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ fontSize: '2rem', display: 'block', marginBottom: 16 }}>{c.icon}</span>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: 16 }}>{c.title}</h3>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {c.tests.map(t => (
                      <li key={t} style={{ fontSize: '0.85rem', color: 'var(--text-mid)', paddingLeft: 14, position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0, top: 6, width: 5, height: 5, background: 'var(--primary-light)', borderRadius: '50%' }} />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: '100px 0', background: 'var(--warm)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', textAlign: 'center', marginBottom: 48 }}>How It Works</h2>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24 }}>
            {[
              { n: '01', t: 'Sample Collection', d: 'Submit at our lab or request on-site collection.' },
              { n: '02', t: 'Testing & Analysis', d: 'NABL-accredited testing per IS codes.' },
              { n: '03', t: 'Quality Review', d: 'Results verified by quality management team.' },
              { n: '04', t: 'Report Delivery', d: 'Certified test report within committed timeline.' },
            ].map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.1}>
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-light)', opacity: 0.3, marginBottom: 16 }}>{s.n}</div>
                  <h4 style={{ fontSize: '1rem', marginBottom: 8 }}>{s.t}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-mid)' }}>{s.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0 100px' }}>
        <div className="container">
          <FadeIn>
            <div style={{ background: 'var(--primary)', borderRadius: 20, padding: 'clamp(40px, 5vw, 64px)', textAlign: 'center' }}>
              <h2 style={{ fontSize: '2rem', color: 'white', marginBottom: 16 }}>Need Material Testing?</h2>
              <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', maxWidth: 480, margin: '0 auto 32px' }}>Get NABL-certified test reports for your construction project.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-accent">Submit Samples</Link>
                <Link to="/contact" className="btn btn-outline" style={{ borderColor: 'white', color: 'white' }}>Inquire About Core Cutting</Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageWrap>
  );
}
